import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import i18next from 'i18next';
import { referenciaDelHallazgo } from 'utils/storeHube/tituloDelHallazgo';


class NotificationService {
  constructor() {
    this.echo = null;
    this.canal = null;
    this.permissionGranted = false;
    this.notificationCallbacks = new Map();
    this.pushSubscription = null;
    this.authToken = null;
    this.baseUrl = null;
  }

  /**
   * Inicializar Laravel Echo con Pusher.
   *
   * `canal` es el nombre COMPLETO del canal privado, con el tenant adentro
   * (`storarmega.<uuid>`), y lo arma el backend: el front no puede derivarlo,
   * porque el id del tenant no se parece al subdominio (`megamix-ar`). Va
   * separado del uuid a propósito: el uuid NO es único entre tenants, y usarlo
   * como nombre de canal hacía que dos sesiones de tenants distintos escucharan
   * el mismo canal de la única app de Pusher que hay. Ver AccountMeResource.
   */
  initialize(canal, authToken, baseUrl) {
    if (this.echo) {
      this.disconnect();
    }

    this.canal = canal;
    this.authToken = authToken;
    this.baseUrl = baseUrl;

    window.Pusher = Pusher;

    // Habilitar logs de Pusher en desarrollo
    if (process.env.NODE_ENV === 'development') {
      Pusher.logToConsole = true;
    }

    this.echo = new Echo({
      broadcaster: 'pusher',
      key: process.env.REACT_APP_PUSHER_APP_KEY,
      cluster: process.env.REACT_APP_PUSHER_APP_CLUSTER,
      forceTLS: true,
      encrypted: true,
      authEndpoint: `${baseUrl}${process.env.REACT_APP_BROADCASTING_AUTH_ENDPOINT}`,
      auth: {
        headers: {
          Authorization: `Bearer ${authToken}`,
          Accept: 'application/json',
        },
      },
    });

    console.log('✅ Laravel Echo inicializado');
    console.log('📡 Config:', {
      key: process.env.REACT_APP_PUSHER_APP_KEY,
      cluster: process.env.REACT_APP_PUSHER_APP_CLUSTER,
      authEndpoint: `${baseUrl}${process.env.REACT_APP_BROADCASTING_AUTH_ENDPOINT}`,
      canal
    });

    return this;
  }

  /**
   * Estado del permiso, SIN pedir nada.
   *
   * Se lee del navegador en vez de confiar en la bandera cacheada: el permiso
   * puede cambiar sin pasar por acá (el banner, o la persona tocando el candado
   * de la barra de direcciones), y una bandera vieja deja los avisos del sistema
   * apagados hasta recargar.
   */
  leerPermisoActual() {
    if (!('Notification' in window)) return false;

    this.permissionGranted = Notification.permission === 'granted';

    return this.permissionGranted;
  }

  // Acá había un `requestNotificationPermission()`. Se eliminó: pedir el permiso
  // desde un servicio, sin un click de la persona atrás, quema el prompt del
  // navegador para todo el origen y deja al botón del banner sin poder mostrar
  // nada. El único que pide es NotificationPermissionBanner, que necesita
  // distinguir los tres desenlaces (granted / denied / el navegador no mostró
  // nada) y por eso usa la API del navegador directamente.

  /**
   * Mostrar notificación del sistema
   */
  async showSystemNotification(title, options = {}) {
    // Se consulta en vivo y no la bandera: el permiso lo concede el banner (o la
    // persona desde el candado del navegador) despues de que este servicio ya
    // arranco, y con la bandera cacheada los avisos quedaban apagados hasta la
    // proxima recarga.
    if (!this.leerPermisoActual()) {
      console.warn('⚠️ Permisos de notificación no concedidos');
      return null;
    }

    // IMPORTANTE: Solo mostrar notificaciones del sistema cuando la página está en background
    // Esto evita duplicados con Pusher Beams
    if (!document.hidden) {
      console.log('ℹ️ Página visible (foreground), omitiendo notificación del sistema para evitar duplicados');
      return null;
    }

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/logo192.png',
      vibrate: [200, 100, 200],
      requireInteraction: false,
      silent: false, // Con sonido
      ...options,
    };

    console.log('🔔 Mostrando notificación del sistema:', title);

    try {
      // Intentar usar Service Worker para notificaciones (mejor para background)
      const registration = await navigator.serviceWorker.getRegistration();

      if (registration && registration.active) {
        console.log('📱 Usando Service Worker para notificación');
        await registration.showNotification(title, defaultOptions);
        console.log('✅ Notificación mostrada vía Service Worker');
        return true;
      }
    } catch (error) {
      console.warn('⚠️ Service Worker no disponible:', error.message);
    }

    // Fallback a Notification API directa (funciona en desarrollo)
    try {
      console.log('📱 Usando Notification API directa (con sonido)');
      const notification = new Notification(title, defaultOptions);

      // Agregar evento de click para abrir la aplicación y navegar al ticket
      notification.onclick = () => {
        window.focus();
        notification.close();

        // Navegar al ticket si existe ticketId en los datos
        if (defaultOptions.data?.ticketId) {
          const ticketId = defaultOptions.data.ticketId;
          window.location.href = `/tickets/inbox?ticket=${ticketId}`;
        }
      };

      console.log('✅ Notificación del sistema mostrada correctamente');
      return true;
    } catch (e) {
      console.error('❌ Error mostrando notificación:', e);
      return null;
    }
  }

  /**
   * Disparar evento personalizado para refrescar tickets
   * Este evento puede ser escuchado por cualquier componente
   */
  dispatchTicketRefreshEvent(eventType, eventData) {
    const customEvent = new CustomEvent('ticket-notification-received', {
      detail: {
        type: eventType,
        data: eventData,
        timestamp: Date.now()
      }
    });
    window.dispatchEvent(customEvent);
  
  }

  /**
   * Suscribirse al canal privado del usuario
   */
  subscribeToUserChannel(callbacks = {}) {
    if (!this.echo || !this.canal) {
      console.error('Echo no inicializado o canal de notificaciones no proporcionado');
      return this;
    }

    const channel = this.echo.private(this.canal);

    // Ticket: Nuevo mensaje
    channel.listen('.ticket-new-message', (event) => {
      console.log('📨 Nuevo mensaje en ticket:', event);

      this.showSystemNotification(`${i18next.t('new_comment_on_ticket')} ${i18next.t('ticket_alias')}`, {
        body: `${i18next.t('ticket_alias')} #${event.payload.ticket_reply?.ticket_id || 'N/A'}: Nuevo comentario recibido`,
        tag: `ticket-message-${event.payload.ticket_reply?.ticket_id}`,
        data: { type: 'ticket-message', ...event.payload },
      });

      // Disparar evento de refresh
      this.dispatchTicketRefreshEvent('ticket-new-message', event);

      if (callbacks.onTicketNewMessage) {
        callbacks.onTicketNewMessage(event);
      }
    });

    // Ticket: Creado
    channel.listen('.ticket-created', (event) => {
      console.log('🎫 Ticket creado:', event);

      this.showSystemNotification(`Nuevo ${i18next.t('ticket_alias')} creado`, {
        body: referenciaDelHallazgo(event.payload.ticket, i18next.t('ticket_alias')),
        tag: `ticket-created-${event.payload.ticket?.id}`,
        data: { type: 'ticket-created', ...event.payload },
      });

      // Disparar evento de refresh
      this.dispatchTicketRefreshEvent('ticket-created', event);

      if (callbacks.onTicketCreated) {
        callbacks.onTicketCreated(event);
      }
    });
    
    // Ticket: Asignado
    channel.listen('.ticket-assigned', (event) => {
      console.log('📋 Ticket asignado:', event);

      this.showSystemNotification(`${i18next.t('ticket_alias')} asignado`, {
        body: `Se te ha asignado ${i18next.t('ticket_alias')} #${event.payload.ticket?.id || 'N/A'}`,
        tag: `ticket-assigned-${event.payload.ticket?.id}`,
        data: { type: 'ticket-assigned', ...event.payload },
      });

      // Disparar evento de refresh para actualizar la lista de tickets
      this.dispatchTicketRefreshEvent('ticket-assigned', event);

      if (callbacks.onTicketAssigned) {
        callbacks.onTicketAssigned(event);
      }
    });

    // Ticket: Cambio de estado
    channel.listen('.ticket-change-status', (event) => {
      console.log('🔄 Estado de ticket cambiado:', event);

      this.showSystemNotification(`Estado de ${i18next.t('ticket_alias')} actualizado`, {
        body: `${i18next.t('ticket_alias')} #${event.payload.ticket?.id || 'N/A'} cambió de estado`,
        tag: `ticket-status-${event.payload.ticket?.id}`,
        data: { type: 'ticket-status', ...event.payload },
      });

      // Disparar evento de refresh
      this.dispatchTicketRefreshEvent('ticket-change-status', event);

      if (callbacks.onTicketChangeStatus) {
        callbacks.onTicketChangeStatus(event);
      }
    });

    // Ticket: Expirado
    channel.listen('.ticket-expired', (event) => {
      console.log('⏰ Ticket expirado:', event);

      this.showSystemNotification(`${i18next.t('ticket_alias')} expirado`, {
        body: `${i18next.t('ticket_alias')} #${event.payload.ticket?.id || 'N/A'} ha expirado`,
        tag: `ticket-expired-${event.payload.ticket?.id}`,
        data: { type: 'ticket-expired', ...event.payload },
        requireInteraction: true,
      });

      // Disparar evento de refresh
      this.dispatchTicketRefreshEvent('ticket-expired', event);

      if (callbacks.onTicketExpired) {
        callbacks.onTicketExpired(event);
      }
    });

    console.log(`✅ Suscrito al canal privado: ${this.canal}`);
    return this;
  }

  /**
   * Desconectar de Pusher
   */
  disconnect() {
    if (this.echo) {
      this.echo.disconnect();
      this.echo = null;
      console.log('❌ Desconectado de Laravel Echo');
    }
  }

  /**
   * Verificar si está conectado
   */
  isConnected() {
    return this.echo !== null;
  }

  /**
   * Obtener el canal del usuario
   */
  getChannel() {
    if (!this.echo || !this.canal) {
      return null;
    }
    return this.echo.private(this.canal);
  }

  /**
   * Convertir clave VAPID de base64 a Uint8Array
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Registrar suscripción de push para notificaciones en segundo plano
   * IMPORTANTE: El backend debe tener configuradas las VAPID keys
   */
  async registerPushSubscription() {
    try {
      // Verificar soporte de Service Worker y Push
      if (!('serviceWorker' in navigator)) {
        console.warn('⚠️ Service Worker no soportado');
        return null;
      }

      if (!('PushManager' in window)) {
        console.warn('⚠️ Push notifications no soportadas');
        return null;
      }

      // Obtener el Service Worker registration
      const registration = await navigator.serviceWorker.ready;
      console.log('📱 Service Worker listo para push subscription');

      // Verificar si ya existe una suscripción
      let subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        console.log('✅ Ya existe una suscripción de push:', subscription);
        this.pushSubscription = subscription;
        return subscription;
      }

      // VAPID public key - DEBE venir del backend
      // Por ahora usamos una key temporal, el backend debe proporcionar esto
      const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;

      if (!vapidPublicKey) {
        console.warn('⚠️ VAPID public key no configurada en .env');
        console.warn('⚠️ Agrega REACT_APP_VAPID_PUBLIC_KEY en tu archivo .env');
        console.warn('⚠️ El backend debe generar las VAPID keys usando: php artisan webpush:vapid');
        return null;
      }

      // Crear nueva suscripción
      const convertedVapidKey = this.urlBase64ToUint8Array(vapidPublicKey);

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true, // Todas las notificaciones push deben ser visibles
        applicationServerKey: convertedVapidKey
      });

      console.log('🎉 Nueva suscripción de push creada:', subscription);
      this.pushSubscription = subscription;

      // Enviar la suscripción al backend
      await this.sendSubscriptionToBackend(subscription);

      return subscription;
    } catch (error) {
      console.error('❌ Error registrando push subscription:', error);
      if (error.name === 'NotAllowedError') {
        console.warn('⚠️ Usuario denegó permisos de notificación');
      }
      return null;
    }
  }

  /**
   * Enviar suscripción de push al backend para guardarla
   */
  async sendSubscriptionToBackend(subscription) {
    try {
      const response = await fetch(`${this.baseUrl}/api/backoffice/push-subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.authToken}`,
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          canal: this.canal
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Suscripción enviada al backend:', data);
      return data;
    } catch (error) {
      console.error('❌ Error enviando suscripción al backend:', error);
      console.warn('⚠️ El backend debe tener el endpoint: POST /api/backoffice/push-subscriptions');
      return null;
    }
  }

  /**
   * Desuscribirse de push notifications
   */
  async unsubscribePush() {
    try {
      if (!this.pushSubscription) {
        const registration = await navigator.serviceWorker.ready;
        this.pushSubscription = await registration.pushManager.getSubscription();
      }

      if (this.pushSubscription) {
        await this.pushSubscription.unsubscribe();
        console.log('✅ Desuscrito de push notifications');
        this.pushSubscription = null;
        return true;
      }

      return false;
    } catch (error) {
      console.error('❌ Error desuscribiendo:', error);
      return false;
    }
  }
}

// Exportar instancia singleton
export const notificationService = new NotificationService();
export default notificationService;
